import type { DayLoadDto } from '@konvert/shared';
import { formatMinutesRu } from '@konvert/domain';
import { t } from '../lib/i18n.js';

/** Time bar and the three energy "stamps" (SPEC 11.2). */
export function LoadBar({ load }: { load: DayLoadDto }): JSX.Element {
  const ratio = Math.min(1, load.max_minutes ? load.time_load_minutes / load.max_minutes : 0);
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-ink">
          {formatMinutesRu(load.time_load_minutes)}{' '}
          <span className="text-ink-soft">
            {t('load.of')} {formatMinutesRu(load.max_minutes)}
          </span>
        </span>
        <EnergyStamps points={load.energy_points} max={load.max_energy_points} />
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--paper-deep)]">
        <div
          className={`h-full rounded-full transition-[width] duration-300 ${
            load.exceeded ? 'bg-brick' : 'bg-[var(--kraft-dark)]'
          }`}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
      {load.exceeded ? (
        <p className="text-xs text-brick">{t('load.exceeded')}</p>
      ) : null}
      {load.unestimated_count > 0 ? (
        <p className="text-xs text-ink-soft">
          {load.unestimated_count} {t('load.unestimated')}
        </p>
      ) : null}
    </div>
  );
}

export function EnergyStamps({ points, max }: { points: number; max: number }): JSX.Element {
  const filled = Math.min(points, max);
  return (
    <span className="flex items-center gap-1" aria-label={`${t('load.energy')} ${points}/${max}`}>
      {Array.from({ length: max }, (_, i) => (
        <span
          key={i}
          className={`inline-block h-3 w-3 rounded-[3px] border ${
            i < filled ? 'border-transparent bg-amber' : 'border-[var(--line)] bg-transparent'
          }`}
        />
      ))}
      {points > max ? <span className="text-xs text-brick">+{points - max}</span> : null}
    </span>
  );
}
